import { motion, PanInfo, useDragControls, VariantLabels } from "framer-motion";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  MdClose,
  MdDragIndicator,
  MdOutlineDownload,
  MdOutlinePushPin,
} from "react-icons/md";
import { fetchPdf } from "services/filesRequests";
import { useParams } from "react-router-dom";
import PDFViewer from "../../utils/PDFViewer";
import { debounce } from "lodash";
import { ScratchEnvironmentContext } from "components/utils/Contexts";

// Constants
/**
 * Used for defining a small "padding" between the borders of the screen
 * and the borders of the PDF panel
 */
const PDF_BORDER_CONSTRAIN = 12; //px
/**
 * Used to define the minimum size of the panel for both, width and height
 */
const MIN_PANEL_SIZE = 290; // px

interface PDFDraggablePanelProps {
  pdf: string;
  handleClosePanel: () => void;
  initial?: object | string | boolean | VariantLabels;
  animate?: object;
  exit?: object;
  style?: object;
  transition?: object;
  panelPosition?: { x: number; y: number };
  setPanelPosition?: React.Dispatch<
    React.SetStateAction<{ x: number; y: number }>
  >;
}

const PDFDraggablePanel: React.FC<PDFDraggablePanelProps> = ({
  pdf,
  handleClosePanel,
  initial = {},
  animate = {},
  exit = {},
  style = {},
  transition = {},
  panelPosition,
  setPanelPosition,
}) => {
  // States definition
  const [size, setSize] = useState({
    width: 494,
    height: 0.8 * window.innerHeight,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggable, setIsDraggable] = useState(true);
  const [constraints, setConstraints] = useState({
    top: PDF_BORDER_CONSTRAIN,
    left: PDF_BORDER_CONSTRAIN,
    right: PDF_BORDER_CONSTRAIN,
    bottom: PDF_BORDER_CONSTRAIN,
  });
  const [animateState, setAnimateState] = useState(animate);
  // State needed becuase of the animation of the PDF growing
  const [triggerMeasure, setTriggerMeasure] = useState(false);

  // Resize states
  const [isResizing, setIsResizing] = useState(false);
  const [currentHandle, setCurrentHandle] = useState<
    "n" | "s" | "w" | "e" | "nw" | "ne" | "sw" | "se" | null
  >(null);
  const [startResizeMousePosition, setStartResizeMousePosition] = useState({
    x: 0,
    y: 0,
  });
  const [startSize, setStartSize] = useState({ width: 0, height: 0 });
  const [startResizePanelPosition, setStartResizePanelPosition] = useState({
    x: 0,
    y: 0,
  });

  // Draggable controls for the drag icon
  const controls = useDragControls();

  // Define a reference for the PDF panel
  const pdfPanelRef = useRef<HTMLDivElement>(null);

  // Get the parameters from the url
  const { moduleOrProject, id } = useParams();

  // Get the states defined by context
  const { setIsDraggingResizingPanel } = useContext(ScratchEnvironmentContext);

  /**
   * Function used to update the limits of the panel based on the
   * window size
   */
  useEffect(() => {
    // Dynamically calculate the viewport size
    const updateConstraints = () => {
      setConstraints({
        top: PDF_BORDER_CONSTRAIN,
        left: PDF_BORDER_CONSTRAIN,
        right:
          window.innerWidth -
          PDF_BORDER_CONSTRAIN -
          (pdfPanelRef.current ? pdfPanelRef.current.clientWidth : 0),
        bottom:
          window.innerHeight -
          PDF_BORDER_CONSTRAIN -
          (pdfPanelRef.current ? pdfPanelRef.current.clientHeight : 0),
      });
    };

    // Update constraints on load and resize
    updateConstraints();
    window.addEventListener("resize", updateConstraints);

    return () => window.removeEventListener("resize", updateConstraints);
  }, [triggerMeasure, size]);

  /**
   * Function to execute when clicking the PDF download button
   * @returns
   */
  const handleDownloadPDFFile = () => {
    // If there is no pdf, then prevent to execute this function
    if (!pdf) return;

    fetchPdf(moduleOrProject || "", id || "").then((url: string) => {
      // Create a temporary anchor element
      const link = document.createElement("a");
      link.href = url;

      // Set the donwload name
      link.download = pdf;

      // Create the element, click it and the remove it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // And finally revoke the URL
      URL.revokeObjectURL(url);
    });
  };

  /**
   * Function used to update the panel position if a set function
   * is defined
   * @param event
   * @param info
   */
  const handleDragPanel = (
    event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    // Change the cursor
    document.body.style.cursor = "grabbing";

    // Check the existance of the setPanelPosition setter
    if (setPanelPosition) {
      setPanelPosition({ x: info.point.x, y: info.point.y });
    }
  };

  /**
   * Function used to manage the resizing of the element
   */
  const handleResizeMouseMove = useCallback(
    debounce((event: any) => {
      // Check if we are resizing
      if (!isResizing) return;

      // Define the delta
      const deltaX = event.clientX - startResizeMousePosition.x;
      const deltaY = event.clientY - startResizeMousePosition.y;

      // Define the output parameters
      let newWidth = startSize.width;
      let newHeight = startSize.height;
      let newX = startResizePanelPosition.x;
      let newY = startResizePanelPosition.y;

      // Resize based on the selected handle
      switch (currentHandle) {
        case "e":
          newWidth = Math.max(startSize.width + deltaX, MIN_PANEL_SIZE);
          break;
        case "w":
          // Check if the limit was achieved
          if (startSize.width - deltaX > MIN_PANEL_SIZE) {
            newWidth = startSize.width - deltaX;
            newX = startResizePanelPosition.x + deltaX;
          } else {
            newWidth = MIN_PANEL_SIZE;
            // Add the limit deltaX
            newX = newX + (startSize.width - MIN_PANEL_SIZE);
          }
          break;
        case "s":
          newHeight = Math.max(startSize.height + deltaY, MIN_PANEL_SIZE);
          break;
        case "n":
          // Check if the limit was achieved
          if (startSize.height - deltaY > MIN_PANEL_SIZE) {
            newHeight = startSize.height - deltaY;
            newY = startResizePanelPosition.y + deltaY;
          } else {
            newHeight = MIN_PANEL_SIZE;
            // Add the limit deltaX
            newY = newY + (startSize.height - MIN_PANEL_SIZE);
          }
          break;
        case "se":
          newWidth = Math.max(startSize.width + deltaX, MIN_PANEL_SIZE);
          newHeight = Math.max(startSize.height + deltaY, MIN_PANEL_SIZE);
          break;
        case "sw":
          newHeight = Math.max(startSize.height + deltaY, MIN_PANEL_SIZE);

          // Check if the limit was achieved
          if (startSize.width - deltaX > MIN_PANEL_SIZE) {
            newWidth = startSize.width - deltaX;
            newX = startResizePanelPosition.x + deltaX;
          } else {
            newWidth = MIN_PANEL_SIZE;
            // Add the limit deltaX
            newX = newX + (startSize.width - MIN_PANEL_SIZE);
          }
          break;
        case "ne":
          newWidth = Math.max(startSize.width + deltaX, MIN_PANEL_SIZE);

          // Check if the limit was achieved
          if (startSize.height - deltaY > MIN_PANEL_SIZE) {
            newHeight = startSize.height - deltaY;
            newY = startResizePanelPosition.y + deltaY;
          } else {
            newHeight = MIN_PANEL_SIZE;
            // Add the limit deltaX
            newY = newY + (startSize.height - MIN_PANEL_SIZE);
          }
          break;
        case "nw":
          // Check if the limit was achieved for the x dimension
          if (startSize.width - deltaX > MIN_PANEL_SIZE) {
            newWidth = startSize.width - deltaX;
            newX = startResizePanelPosition.x + deltaX;
          } else {
            newWidth = MIN_PANEL_SIZE;
            // Add the limit deltaX
            newX = newX + (startSize.width - MIN_PANEL_SIZE);
          }

          // Check if the limit was achieved for the y dimension
          if (startSize.height - deltaY > MIN_PANEL_SIZE) {
            newHeight = startSize.height - deltaY;
            newY = startResizePanelPosition.y + deltaY;
          } else {
            newHeight = MIN_PANEL_SIZE;
            // Add the limit deltaX
            newY = newY + (startSize.height - MIN_PANEL_SIZE);
          }
          break;
      }

      // Define the new size
      setSize({ width: newWidth, height: newHeight });
      setPanelPosition && setPanelPosition({ x: newX, y: newY });

      // Get everything but the position of the animate parameter
      const { x: xOld, y: yOld, ...animateProps } = animate as any;
      setAnimateState({
        ...animateProps,
        x: newX,
        y: newY,
      });
    }),
    [
      isResizing,
      currentHandle,
      startResizeMousePosition,
      startSize,
      startResizePanelPosition,
    ]
  );

  /**
   * Function used to set up the parameters used for the resizing
   * process
   */
  const handleResizeMouseDown = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
      handle: "n" | "s" | "w" | "e" | "nw" | "ne" | "sw" | "se" | null
    ) => {
      // Prevent default behaviour
      event.preventDefault();

      // Get the left and top value from the PDF panel
      const { left, top } = pdfPanelRef.current
        ? pdfPanelRef.current.getBoundingClientRect()
        : { left: 0, top: 0 };

      // Setting up
      setIsResizing(true);
      setCurrentHandle(handle);
      setStartResizeMousePosition({ x: event.clientX, y: event.clientY });
      setStartResizePanelPosition({ x: left, y: top });
      setStartSize({ width: size.width, height: size.height });
    },
    [pdfPanelRef.current, size]
  );

  /**
   * Function used to set up the parameters used for the dragging
   * process
   */
  const handleDragMouseDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isDraggable) {
      controls.start(event); // Initiates drag
    }

    // Prevent default behaviour
    event.preventDefault();
    // Change the cursor
    document.body.style.cursor = "grabbing";
    // And set the drag state
    setIsDragging(true);

    // Add a listener to change this when pressing mouse up
    window.addEventListener("mouseup", handleDragMouseUp);
    return () => window.removeEventListener("mouseup", handleDragMouseUp);
  };

  /**
   * Function used to turn off the parameters that are activated
   * during the resizing process
   */
  const handleResizeMouseUp = useCallback(() => {
    setIsResizing(false);
    setCurrentHandle(null);
  }, []);

  /**
   * Function used to turn off the parameters that are activated
   * during the dragging process
   */
  const handleDragMouseUp = useCallback(() => {
    // Set dragging to false
    setIsDragging(false);
    // And reset the cursor
    document.body.style.cursor = "auto";
  }, [document]);

  useEffect(() => {
    if (isResizing) {
      window.addEventListener("mousemove", handleResizeMouseMove);
      window.addEventListener("mouseup", handleResizeMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleResizeMouseMove);
      window.removeEventListener("mouseup", handleResizeMouseUp);
    };
  }, [isResizing, handleResizeMouseMove]);

  /**
   * Function executed after the each animation is completed
   */
  const handleAnimationComplete = () => {
    // Get everything but the position of the animate parameter
    const { x: xOld, y: yOld, ...animateProps } = animate as any;

    // Get the left and top value from the PDF panel
    const { x, y } = pdfPanelRef.current
      ? pdfPanelRef.current.getBoundingClientRect()
      : { x: 0, y: 0 };

    // Set the new animate state to prevent being moved to the
    // original value of animate
    setAnimateState({
      ...animateProps,
      x,
      y,
    });

    // Raise the flag for the triggering measure
    setTriggerMeasure(true);
  };

  /**
   * Function used to update the state used to coordinate the definition
   * of the "pointer-events-{x}" property of the Scratch editor
   */
  useEffect(() => {
    setIsDraggingResizingPanel &&
      setIsDraggingResizingPanel(isDragging || isResizing);
  }, [isDragging, isResizing]);

  return (
    <motion.div
      style={{ ...style, ...size, ...panelPosition }}
      className="fixed w-fit h-fit inset-0 bg-white border border-neutral-50 z-30 shadow-[0px_0px_8px_0px] shadow-neutral-300 rounded-lg overflow-hidden"
      ref={pdfPanelRef}
      drag={true}
      dragConstraints={constraints}
      dragElastic={0.5}
      dragListener={false}
      dragMomentum={false}
      dragControls={controls}
      initial={isResizing ? undefined : initial}
      animate={isResizing ? undefined : { ...animateState, ...size }}
      exit={isResizing ? undefined : exit}
      transition={transition}
      onAnimationComplete={handleAnimationComplete}
      onDrag={handleDragPanel}
    >
      <div className="max-h-screen max-w-[100vw] inline-flex flex-col w-full h-full rounded-lg p-4 relative">
        <div className="self-stretch inline-flex justify-center items-center">
          <div
            className={`w-8 h-8 inline-flex justify-center items-center relative ${
              isDragging ? "cursor-grabbing" : "cursor-grab"
            } -rotate-90${
              isDraggable ? " hover:bg-neutral-50 rounded-full" : ""
            } `}
            onMouseDown={handleDragMouseDown}
          >
            <MdDragIndicator
              className={`w-6 h-6 ${
                isDraggable ? "fill-neutral-700" : "fill-Subtle"
              }`}
            />
          </div>
        </div>
        <div className="inline-flex flex-col gap-[10px] w-full h-full">
          <div className="h-9 justify-between items-center gap-2 inline-flex">
            <div className="text-neutral-700 text-[23px] font-semibold font-sans leading-7 select-none">
              Instructions
            </div>
            <div className="self-stretch justify-end items-center gap-2 inline-flex">
              <div
                className="p-2 bg-neutral-100 hover:bg-neutral-200 ease-in-out duration-150 rounded-full justify-center items-center gap-2 flex cursor-pointer"
                onClick={handleDownloadPDFFile}
              >
                <MdOutlineDownload className="w-5 h-5 fill-neutral-700" />
              </div>
              <div
                className="p-2 bg-neutral-100 hover:bg-neutral-200 ease-in-out duration-150 rounded-full justify-center items-center gap-2 flex cursor-pointer"
                onClick={() => setIsDraggable(!isDraggable)}
              >
                <div className="w-5 h-5 relative">
                  <MdOutlinePushPin
                    className={`w-full h-full ${
                      isDraggable ? "fill-neutral-700" : "fill-Subtle"
                    }`}
                  />
                </div>
              </div>
              <div
                className="p-2 bg-neutral-100 hover:bg-neutral-200 ease-in-out duration-150 rounded-full justify-center items-center gap-2 flex cursor-pointer"
                onClick={handleClosePanel}
              >
                <MdClose className="w-5 h-5 fill-neutral-700" />
              </div>
            </div>
          </div>
          <PDFViewer isPaning={isResizing} isVisible={true} />
        </div>

        {/* Edge handles */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 cursor-n-resize"
          data-handle="n"
          onMouseDown={(event) => handleResizeMouseDown(event, "n")}
        />
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-1 cursor-s-resize"
          data-handle="s"
          onMouseDown={(event) => handleResizeMouseDown(event, "s")}
        />
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 h-full w-1 cursor-w-resize"
          data-handle="w"
          onMouseDown={(event) => handleResizeMouseDown(event, "w")}
        />
        <div
          className="absolute right-0 top-1/2 -translate-y-1/2 h-full w-1 cursor-e-resize"
          data-handle="e"
          onMouseDown={(event) => handleResizeMouseDown(event, "e")}
        />

        {/* Corner handles */}
        <div
          className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize"
          data-handle="nw"
          onMouseDown={(event) => handleResizeMouseDown(event, "nw")}
        />
        <div
          className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize"
          data-handle="ne"
          onMouseDown={(event) => handleResizeMouseDown(event, "ne")}
        />
        <div
          className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize"
          data-handle="sw"
          onMouseDown={(event) => handleResizeMouseDown(event, "sw")}
        />
        <div
          className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize"
          data-handle="se"
          onMouseDown={(event) => handleResizeMouseDown(event, "se")}
        />
      </div>
    </motion.div>
  );
};

export default PDFDraggablePanel;
